/**
 * Separation of Duties Utility Functions
 *
 * This file contains utility functions for handling separation of duties
 * logic to avoid code duplication.
 */

import { SEPARATION_OF_DUTIES_STATUSES, INVOICE_STATUSES } from '@constants/invoiceStatus';
import { safeIdComparison } from './idComparison';

// Use centralized status constants
const { ADDING, IR, INVOICING } = INVOICE_STATUSES;

export interface AuditLog {
  action: string;
  changes?: {
    after?: {
      status?: string;
    };
  };
  user_id?: string;
}

export interface SeparationOfDutiesParams {
  currentStatus: string;
  userId?: string;
  isAdmin: boolean;
  isFinance?: boolean;
  auditLogs?: AuditLog[];
  getUserWhoMovedToStatus: (status: string) => string | null;
}

/**
 * Check if separation of duties rules should block a user from editing
 * @param params - Parameters for separation of duties check
 * @returns true if user should be blocked, false if allowed
 */
export const shouldBlockUserForSeparationOfDuties = ({
  currentStatus,
  userId,
  isAdmin,
  isFinance,
  getUserWhoMovedToStatus
}: SeparationOfDutiesParams): boolean => {
  // Admin and Finance users bypass separation of duties restrictions
  if (isAdmin || isFinance) {
    return false;
  }

  // Only apply separation of duties to specific statuses
  if (!(SEPARATION_OF_DUTIES_STATUSES as readonly string[]).includes(currentStatus)) {
    return false;
  }

  // For 'adding' status, separation of duties doesn't apply because:
  // - Users need to be able to work in 'adding' after moving unprocessed→adding
  // - The separation applies when they move adding→ir (they can't edit in 'ir')
  if (currentStatus === ADDING) {
    return false;
  }

  // Get the previous status based on current status to find who moved TO current status
  let previousStatus: string;
  switch (currentStatus) {
    case IR:
      previousStatus = ADDING;
      break;
    case INVOICING:
      previousStatus = IR;
      break;
    default:
      // For other statuses, we can't determine the previous status easily
      return false;
  }
  
  // Get the user who moved the invoice FROM the previous status TO the current status
  const userWhoMovedToCurrentStatus = getUserWhoMovedToStatus(previousStatus);
  
  // If audit data is not loaded yet (getUserWhoMovedToStatus returns null),
  // be permissive and don't block (let the user work while data loads)
  // This prevents the race condition where field becomes disabled when audit data loads
  if (userWhoMovedToCurrentStatus === null) {
    return false;
  }
  
  // If the current user is the one who moved the invoice TO this status,
  // they should be blocked from editing in this status (separation of duties)
  // Use safe ID comparison to handle string/number userId comparisons
  const shouldBlock = safeIdComparison(userWhoMovedToCurrentStatus, userId);
  
  return shouldBlock;
};

/**
 * Check if a user can edit workflow fields based on assignment and separation of duties
 * @param params - Parameters for workflow field editing check
 * @returns true if user can edit, false if blocked
 */
export const canEditWorkflowFields = ({
  currentStatus,
  userId: _userId,
  isAdmin,
  isFinance,
  isAssignedUser,
  getUserWhoMovedToStatus: _getUserWhoMovedToStatus
}: SeparationOfDutiesParams & { isAssignedUser: boolean }): boolean => {
  // For assignment-required statuses, prioritize admin/finance bypass
  if (currentStatus === ADDING || currentStatus === IR || currentStatus === INVOICING) {
    // Admin and Finance users bypass ALL restrictions (assignment + separation of duties)
    if (isAdmin || isFinance) {
      return true;
    }
    
    // For non-admin/finance users, they must be assigned to edit
    if (!isAssignedUser) {
      return false;
    }
    
    // BUSINESS RULE: Being assigned takes precedence over separation of duties
    // If you are assigned to work on an invoice in a specific status, you should be able to
    // edit the primary fields for that status, even if you moved it there.
    // This is because assignment is an explicit permission grant.
    return true;
  }

  // For other statuses, return true (caller should handle role-based permissions)
  return true;
};
